"use client"

import {
  PARTNER_AGENT_LS_CAPTURED_AT_KEY,
  PARTNER_AGENT_LS_KEY,
  getPartnerAttributionWindowDays,
} from "@/lib/partners/constants"
import { readPartnerAgentCodeFromSearchParams } from "@/lib/partners/read-agent-param"
import {
  buildPartnerAgentCodeSetCookieValue,
  normalizePartnerAgentCode,
  readPartnerAgentCodeFromDocumentCookie,
} from "@/lib/partners/partner-agent-cookie"
import {
  resolvePartnerAgentCapturedAtAtSignup,
  resolvePartnerAgentCodeAtSignup,
} from "@/lib/partners/resolve-signup-agent"

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

function safeGetLocalStorage(): Storage | null {
  if (!isBrowser()) return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function cookieMaxAgeSeconds(): number {
  return getPartnerAttributionWindowDays() * 24 * 60 * 60
}

function persistPartnerAgentCodeCookie(code: string): void {
  if (!isBrowser()) return
  const secure = window.location.protocol === "https:"
  document.cookie = buildPartnerAgentCodeSetCookieValue(code, secure, cookieMaxAgeSeconds())
}

function readCapturedAtFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  const raw = storage.getItem(PARTNER_AGENT_LS_CAPTURED_AT_KEY)?.trim()
  return raw || null
}

function writeCapturedAtToLocalStorage(iso: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(PARTNER_AGENT_LS_CAPTURED_AT_KEY, iso)
}

function readFromLocalStorage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null
  return normalizePartnerAgentCode(storage.getItem(PARTNER_AGENT_LS_KEY))
}

function writeToLocalStorage(code: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  storage.setItem(PARTNER_AGENT_LS_KEY, code)
}

function readFromUrl(): string | null {
  if (!isBrowser()) return null
  try {
    const url = new URL(window.location.href)
    return readPartnerAgentCodeFromSearchParams(url.searchParams)
  } catch {
    return null
  }
}

/** Último clique em `?agente=` (ou `?corretor=` legado) antes do signup sobrescreve código e reinicia a janela. */
export function capturePartnerAgentFromPage(): void {
  if (!isBrowser()) return

  const fromUrl = readFromUrl()
  if (fromUrl) {
    writeToLocalStorage(fromUrl)
    writeCapturedAtToLocalStorage(new Date().toISOString())
    persistPartnerAgentCodeCookie(fromUrl)
    return
  }

  const fromLs = readFromLocalStorage()
  const fromCookie = readPartnerAgentCodeFromDocumentCookie(document.cookie)
  const existing = fromLs ?? fromCookie
  if (existing) {
    writeToLocalStorage(existing)
    persistPartnerAgentCodeCookie(existing)
  }
}

export function getPartnerAgentCapturedAt(): string | null {
  if (!isBrowser()) return null
  const urlCode = readFromUrl()
  return resolvePartnerAgentCapturedAtAtSignup({
    hasUrlCode: Boolean(urlCode),
    localStorageCapturedAt: readCapturedAtFromLocalStorage(),
  })
}

export function initPartnerAgentCapture(): void {
  capturePartnerAgentFromPage()
}

export function getPartnerAgentSignupPayload(): {
  code: string
  capturedAt: string | null
  source: import("@/lib/partners/resolve-signup-agent").PartnerAgentSignupSource
} | null {
  if (!isBrowser()) return null
  const resolved = resolvePartnerAgentCodeAtSignup({
    urlCode: readFromUrl(),
    localStorageCode: readFromLocalStorage(),
    cookieCode: readPartnerAgentCodeFromDocumentCookie(document.cookie),
  })
  if (!resolved.code || !resolved.source) return null
  return {
    code: resolved.code,
    capturedAt: getPartnerAgentCapturedAt(),
    source: resolved.source,
  }
}
